import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Footprints, Plus, Trash } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { computeStepsWeeklyAverages } from '@/pages/PassosPage';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PassosTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], steps: '' });

  const fetchRecords = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/steps`); setRecords(data || []); } catch { /* noop */ } finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const add = async () => {
    if (!form.steps) return;
    try { await axios.post(`${API}/api/patients/${patientId}/steps`, { date: form.date, steps: parseInt(form.steps, 10) }); toast.success('Passos guardados'); setForm({ date: new Date().toISOString().split('T')[0], steps: '' }); fetchRecords(); } catch { toast.error('Erro ao guardar'); }
  };
  const del = async (id) => {
    const r = records.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de ${r?.date || ''} (${r?.steps || 0} passos).` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/patients/${patientId}/steps/${id}`); toast.success('Eliminado'); fetchRecords(); } catch { /* noop */ }
  };

  const weekly = computeStepsWeeklyAverages(records);
  const chartData = records.slice(0, 14).reverse();
  const maxSteps = Math.max(...chartData.map(r => r.steps), 1);

  return (
    <div className="space-y-4" data-testid="admin-passos-tab">
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <h3 className="font-heading text-sm text-brand-text mb-3 flex items-center gap-2"><Footprints size={16} /> Registar passos</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border w-40" data-testid="admin-steps-date" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Passos</Label><Input type="number" value={form.steps} onChange={e => setForm({ ...form, steps: e.target.value })} placeholder="Ex: 8500" className="rounded-xl border-brand-border w-32" data-testid="admin-steps-value" /></div>
          <button onClick={add} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="admin-add-steps-btn"><Plus size={14} /> Adicionar</button>
        </div>
      </div>

      {chartData.length >= 2 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4">
          <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-3">Evolução</h4>
          <div className="flex items-end justify-between gap-1 h-32">
            {chartData.map(r => (
              <div key={r.id} className="flex-1 flex flex-col items-center justify-end h-full" title={`${r.date}: ${r.steps}`}>
                <div className="w-full rounded-t bg-brand-green" style={{ height: `${Math.max((r.steps / maxSteps) * 100, 2)}%` }} />
                <span className="text-[8px] text-brand-muted mt-1">{r.date?.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {weekly.length > 0 && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-3" data-testid="admin-steps-weekly">
          <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Médias semanais</h4>
          <div className="space-y-1">
            {weekly.slice().reverse().map(b => (
              <div key={b.week} className="flex justify-between text-xs"><span className="text-brand-text">Semana {b.label}</span><strong className="text-brand-green">{b.avg.toLocaleString('pt-PT')} passos</strong></div>
            ))}
          </div>
        </div>
      )}

      {loading ? <p className="text-center text-brand-muted py-6 text-sm">A carregar...</p> : records.length === 0 ? (
        <p className="text-center text-brand-muted py-8 text-sm">Sem registos de passos.</p>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`admin-steps-record-${r.id}`}>
              <span className="text-xs text-brand-muted">{fmtDatePt(r.date)}</span>
              <div className="flex items-center gap-3">
                <strong className="text-brand-text">{(r.steps || 0).toLocaleString('pt-PT')} passos</strong>
                <button onClick={() => del(r.id)} className="text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
